import { useEffect, useState } from 'react';
import { getRoster, RosterEntry, RosterPerson } from '../api/roster';
import Icon from './Icon';

export interface OnCallPerson { role: string; person: RosterPerson; }

// Monday of the current week, as YYYY-MM-DD (matches the roster API).
function currentWeekStart(): string {
  const d = new Date();
  const day = d.getDay();                 // 0 Sun .. 6 Sat
  const diff = day === 0 ? -6 : 1 - day;  // back to Monday
  d.setDate(d.getDate() + diff);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
}

// Shared fetch of this week's roster → the primary person per assigned role.
export function useOnCall() {
  const [onCall, setOnCall] = useState<OnCallPerson[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    getRoster(currentWeekStart())
      .then((r) => {
        if (!alive) return;
        const list = (r.roster || [])
          .filter((e: RosterEntry) => e.primary)
          .map((e: RosterEntry) => ({ role: e.cimtRole, person: e.primary as RosterPerson }));
        setOnCall(list);
      })
      .catch(() => { if (alive) setOnCall([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return { onCall, loading };
}

function Avatar({ name, lead }: { name: string; lead?: boolean }) {
  return (
    <span style={{ position: 'relative', flexShrink: 0, width: 38, height: 38 }}>
      <span style={{
        width: 38, height: 38, borderRadius: '50%',
        display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 700,
        background: lead ? 'var(--primary)' : 'color-mix(in srgb, var(--primary) 12%, transparent)',
        color: lead ? '#fff' : 'var(--primary)',
      }}>{initials(name)}</span>
      {lead && (
        <span aria-hidden style={{
          position: 'absolute', bottom: 0, right: 0,
          width: 11, height: 11, borderRadius: '50%',
          background: 'var(--success)', border: '2px solid var(--card)',
        }} />
      )}
    </span>
  );
}

// Full card — dashboard right column.
export function CimtOnCallCard({ onViewRoster }: { onViewRoster?: () => void }) {
  const { onCall, loading } = useOnCall();
  const top = onCall.slice(0, 4);

  return (
    <div style={{
      background: 'var(--card)', border: '1px solid var(--border)',
      borderRadius: 16, padding: '18px 18px 16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--subtle)' }}>
          CIMT on call
        </span>
        {top.length > 0 && (
          <span style={{
            fontSize: 10.5, fontWeight: 700, color: 'var(--success)',
            background: 'var(--success-soft)', padding: '4px 11px', borderRadius: 999,
          }}>
            Active
          </span>
        )}
      </div>

      {loading ? (
        <div style={{ fontSize: 12.5, color: 'var(--muted)', padding: '6px 0' }}>Loading roster…</div>
      ) : top.length === 0 ? (
        <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5, padding: '2px 0 10px' }}>
          No one is rostered on call this week.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
          {top.map((o, i) => (
            <div key={o.role + i} style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
              <Avatar name={o.person.name} lead={i === 0} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--fg)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {o.person.name}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{o.role}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {onViewRoster && (
        <button onClick={onViewRoster} style={{
          width: '100%', marginTop: 16, padding: '11px 14px',
          background: 'var(--surface)', border: '1px solid var(--border-md)',
          borderRadius: 10, color: 'var(--primary)', fontSize: 13, fontWeight: 700,
          cursor: 'pointer', fontFamily: 'inherit',
        }}>
          View weekly roster
        </button>
      )}
    </div>
  );
}

// Compact — sidebar bottom.
export function CimtOnCallMini({ onClick }: { onClick?: () => void }) {
  const { onCall, loading } = useOnCall();
  const lead = onCall[0];
  if (loading || !lead) return null;
  return (
    <button className="cs-cimt-mini" onClick={onClick} style={{ textAlign: 'left', cursor: onClick ? 'pointer' : 'default', width: '100%' }}>
      <div className="hd"><span className="dot" />CIMT on call</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <Avatar name={lead.person.name} lead />
        <div style={{ minWidth: 0 }}>
          <div className="nm" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lead.person.name}</div>
          <div className="rl">{lead.role}</div>
        </div>
        <span style={{ marginLeft: 'auto', color: 'var(--faint)', display: 'inline-flex' }}><Icon name="chevronRight" size={15} /></span>
      </div>
    </button>
  );
}
